import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, Paper, Alert, CircularProgress, TextField, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton,
  InputAdornment, Chip, Tooltip, ToggleButton, ToggleButtonGroup, Divider, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { BRAND } from "../../styles/theme";
import {
  getMovimientos, getMovimientosHistory, requestMovimientosExport, parseMonto, fmtMonto,
  CMF_CUENTA_DEFAULT, CMF_CUENTA_RE,
  type Movimiento, type MovimientoHist,
} from "../../services/cmf";
import { useCmfAccounts } from "../../services/cmfAccounts";

// Cuenta por defecto (recaudadora). La lista de cuentas conocidas y sus
// etiquetas vienen del backend (GET /cmf/cuentas, hook useCmfAccounts); el
// selector además acepta cualquier número tipeado y suma las cuentas que
// devuelve ConsultarSaldos.
const CUENTA_DEFAULT = CMF_CUENTA_DEFAULT;
// Última cuenta usada: sobrevive al reload / volver a la página. La URL
// (?cuenta=) manda sobre esto para que un link compartido abra esa cuenta.
const LS_CUENTA = "webadmin2.cmf.movimientos.cuenta";
function readCuentaInicial(fromUrl: string | null): string {
  if (fromUrl && CMF_CUENTA_RE.test(fromUrl)) return fromUrl;
  try {
    const v = localStorage.getItem(LS_CUENTA);
    if (v && CMF_CUENTA_RE.test(v)) return v;
  } catch { /* storage bloqueado: default */ }
  return CUENTA_DEFAULT;
}

// Números monoespaciados con cifras tabulares → columnas alineadas, escaneo rápido.
const MONO = '"SF Mono","JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
const DEBITO  = "#C0322B";
const CREDITO = "#1E7A46";

const thSx = {
  fontWeight: 700, fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase",
  color: BRAND.primaryDark, borderBottom: "none", py: 1.2,
} as const;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function fmtFecha(iso: string): { date: string; time: string } {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: iso, time: "" };
  return {
    date: d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }),
    time: d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }),
  };
}
// "10055-DB-Debito por DEBIN..." → { code: "10055", text: "Debito por DEBIN..." }
function splitConcepto(c: string): { code: string; text: string } {
  if (!c) return { code: "", text: "—" };
  const m = c.match(/^(\d{3,6})\s*-\s*[A-Z]{1,3}\s*-\s*(.*)$/);
  if (m) return { code: m[1], text: m[2] };
  const m2 = c.match(/^(\d{3,6})\s*-\s*(.*)$/);
  if (m2) return { code: m2[1], text: m2[2] };
  return { code: "", text: c };
}

// Errores del banco que merecen una explicación humana. El detalle crudo se
// conserva desplegable. 1005 = la cuenta existe pero no está asociada al cliente
// API (x-clientId) con el que se autentica el conector: lo resuelve el banco.
function explicarError(e: unknown, cuenta: string): { msg: string; detail?: string } {
  const raw = String(e);
  if (/codigo\\?"?\s*:\s*\\?"?1005/.test(raw)) {
    return {
      msg: `CMF: la cuenta ${cuenta} no está habilitada para el cliente API de Cucuru (código 1005). ` +
           `Hay que pedirle al banco que la asocie al convenio API.`,
      detail: raw,
    };
  }
  return { msg: raw };
}

type Tipo = "todos" | "deb" | "cred";
// Fuente de datos: "hist" lee la copia local (DynamoDB, act. ~1h, rango completo
// de una) / "live" pega al banco vía connector (tiempo real, 10/pág, ~3s/pág).
type Fuente = "hist" | "live";

// Días de profundidad del histórico local (backfill del job). Antes de eso → live.
const HIST_DEPTH_DAYS = 31;

// ---- Export CSV (separador ";" + decimal con coma → Excel es-AR) ----
const CSV_SEP = ";";
function csvField(v: string): string {
  // [\r\n]+ y no /\r?\n/: el `detalle` de CMF trae un CR huérfano al final
  // ("CONCEPTO: VAR \r") que la regex vieja no matcheaba — quedaba crudo en el
  // CSV y Excel partía cada movimiento en dos filas.
  const s = (v ?? "").replace(/[\r\n]+/g, " ").trim();
  return /[";]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function buildCsv(rows: Movimiento[]): string {
  const head = ["fecha_hora", "fecha_valor", "concepto", "detalle", "comprobante",
                "originante", "moneda", "monto", "saldo_parcial"];
  const lines = [head.join(CSV_SEP)];
  for (const m of rows) {
    lines.push([
      csvField(m.fecha_hora), csvField(m.fecha_valor), csvField(m.concepto),
      csvField(m.detalle ?? ""), csvField(m.comprobante),
      csvField(m.originante?.nro_documento
        ? `${m.originante.tipo_documento ?? ""} ${m.originante.nro_documento}`.trim() : ""),
      csvField(m.moneda),
      String(parseMonto(m.monto)).replace(".", ","),
      String(parseMonto(m.saldo_parcial)).replace(".", ","),
    ].join(CSV_SEP));
  }
  return "\uFEFF" + lines.join("\r\n");   // BOM: Excel detecta UTF-8
}
function downloadCsv(csv: string, filename: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Movimientos() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Cuentas conocidas + etiquetas (servidor). `hasHist` sólo es true para la que
  // indexa el job; el fallback offline ya lo sabe para la recaudadora.
  const { cuentas: cuentasConocidas, label: cmfAccountLabel, hasHist: cmfAccountHasHist, saveLabel } = useCmfAccounts();
  const [cuenta,  setCuentaState] = useState(() => readCuentaInicial(searchParams.get("cuenta")));
  // Sólo la recaudadora tiene Histórico local (el job indexa una sola cuenta).
  // Para cualquier otra, la única fuente posible es "En vivo".
  const cuentaTieneHist = cmfAccountHasHist(cuenta);
  const [fuente,  setFuente]  = useState<Fuente>(() => cuentaTieneHist ? "hist" : "live");
  // Texto tal cual está en el input (puede ser un número a medio tipear).
  const [cuentaInput, setCuentaInput] = useState(cuenta);
  // Opciones del selector: las que devuelve GET /cmf/cuentas (ya incluye las del
  // banco vía ConsultarSaldos, las etiquetadas a mano y la recaudadora). No se
  // llama a getSaldos() aparte: sería una segunda consulta al banco por carga y
  // CMF limita a ~10/min compartidas con el job.
  const opcionesCuenta = useMemo(() => cuentasConocidas.map((a) => a.numero), [cuentasConocidas]);
  // Una cuenta que no está en el listado (el banco no la devuelve y nadie la
  // etiquetó) se agrega poniéndole etiqueta: eso la persiste en el servidor y
  // GET /cmf/cuentas la lista desde entonces (source "manual"). El selector
  // ofrece esa acción como última opción cuando el número tipeado es válido y
  // no figura. Prefijo interno, nunca se muestra ni se manda al banco.
  const ADD_PREFIX = "__add__:";

  // Edición de la etiqueta de la cuenta actual (lapicito del título).
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");
  const [labelSaving, setLabelSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const abrirLabel = useCallback(() => { setLabelDraft(cmfAccountLabel(cuenta) ?? ""); setLabelOpen(true); }, [cuenta, cmfAccountLabel]);
  const guardarLabel = useCallback(async () => {
    setLabelSaving(true);
    try {
      const r = await saveLabel(cuenta, labelDraft.trim());
      setToast(r.label ? `Etiqueta de la cuenta ${cuenta} guardada: "${r.label}"` : `Etiqueta de la cuenta ${cuenta} borrada`);
      setLabelOpen(false);
    } catch (e) {
      setToast(`No se pudo guardar la etiqueta: ${String(e)}`);
    } finally {
      setLabelSaving(false);
    }
  }, [cuenta, labelDraft, saveLabel]);

  // Cambiar de cuenta: persiste en URL + localStorage y, si la nueva no tiene
  // histórico, pasa a "En vivo" (y vuelve a Histórico al volver a la recaudadora).
  const setCuenta = useCallback((v: string) => {
    const n = v.trim();
    setCuentaState(n);
    setCuentaInput(n);
    if (CMF_CUENTA_RE.test(n)) {
      try { localStorage.setItem(LS_CUENTA, n); } catch { /* ignore */ }
      setSearchParams((prev) => { const q = new URLSearchParams(prev); q.set("cuenta", n); return q; }, { replace: true });
      setFuente(cmfAccountHasHist(n) ? "hist" : "live");
    }
  }, [setSearchParams, cmfAccountHasHist]);
  const [desde,   setDesde]   = useState(isoDaysAgo(1));   // últimas 24hs por defecto
  const [hasta,   setHasta]   = useState(isoDaysAgo(0));
  // --- live (banco): SOLO la página actual, 10/request ---
  const [pagina,  setPagina]  = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [movs,    setMovs]    = useState<Movimiento[]>([]);
  // --- histórico (base local): acumula batches de 500 vía cursor ---
  const [histRows,   setHistRows]   = useState<MovimientoHist[]>([]);
  const [histCursor, setHistCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [info,    setInfo]    = useState<string | null>(null);
  const [didSearch, setDidSearch] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filtros client-side sobre las filas cargadas (CMF no permite buscar server-side).
  const [q,       setQ]       = useState("");
  const [tipo,    setTipo]    = useState<Tipo>("todos");
  const [montoMin, setMontoMin] = useState("");
  const [montoMax, setMontoMax] = useState("");

  // Live: paginación server-side, una página (10 movs) por request. CMF devuelve
  // 10/página y no respeta el page size; traer todo el rango en vivo es inviable.
  const buscarLive = useCallback(async (pag: number, cta: string = cuenta) => {
    if (!cta) { setError("Ingresá una cuenta."); return; }
    if (!CMF_CUENTA_RE.test(cta)) { setError("Cuenta inválida: solo dígitos (6 a 12)."); return; }
    setLoading(true); setError(null); setErrorDetail(null);
    try {
      const r = await getMovimientos({ numero_cuenta: cta, fecha_desde: desde, fecha_hasta: hasta, pagina: pag });
      setMovs(r.movimientos);
      setTotalPaginas(r.total_paginas || 1);
      setPagina(pag);
      setDidSearch(true);
    } catch (e) {
      const { msg, detail } = explicarError(e, cta);
      setError(msg); setErrorDetail(detail ?? null);
      // Sin resultados válidos no hay nada que paginar: no dejar el "Página 1 / 115"
      // de la búsqueda anterior colgado debajo de una tabla vacía.
      setMovs([]); setTotalPaginas(1); setPagina(1);
    } finally {
      setLoading(false);
    }
  }, [cuenta, desde, hasta]);

  // Histórico: primer batch (500) del rango; "Cargar más" appendea via cursor.
  const buscarHist = useCallback(async (cta: string = cuenta) => {
    if (!cta) { setError("Ingresá una cuenta."); return; }
    if (!CMF_CUENTA_RE.test(cta)) { setError("Cuenta inválida: solo dígitos (6 a 12)."); return; }
    if (!cmfAccountHasHist(cta)) {
      setError(`La cuenta ${cta} no tiene histórico local (el job indexa sólo la ${CUENTA_DEFAULT}). Usá "En vivo".`);
      return;
    }
    setLoading(true); setError(null); setErrorDetail(null);
    try {
      const r = await getMovimientosHistory({ cuenta: cta, from: desde, to: hasta, order: "desc", limit: 500 });
      setHistRows(r.movimientos);
      setHistCursor(r.next_cursor);
      setDidSearch(true);
    } catch (e) {
      setError(String(e)); setHistRows([]); setHistCursor(null);
    } finally {
      setLoading(false);
    }
  }, [cuenta, desde, hasta, cmfAccountHasHist]);

  const cargarMasHist = useCallback(async () => {
    if (!histCursor) return;
    setLoading(true); setError(null);
    try {
      const r = await getMovimientosHistory({ cuenta, from: desde, to: hasta, order: "desc", limit: 500, cursor: histCursor });
      setHistRows((prev) => [...prev, ...r.movimientos]);
      setHistCursor(r.next_cursor);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [cuenta, desde, hasta, histCursor]);

  const buscar = useCallback(
    (pag: number) => (fuente === "hist" ? buscarHist() : buscarLive(pag)),
    [fuente, buscarHist, buscarLive],
  );

  // Buscar con una cuenta recién elegida/tipeada: la commitea (URL, storage,
  // fuente) y busca con ESA cuenta, sin depender de que el estado ya se haya
  // re-renderizado. Si es la misma que ya está, respeta la fuente elegida.
  const buscarCon = useCallback((cta: string) => {
    const n = cta.trim();
    if (n === cuenta) { buscar(1); return; }
    setCuenta(n);
    if (!CMF_CUENTA_RE.test(n)) { setError("Cuenta inválida: solo dígitos (6 a 12)."); return; }
    if (cmfAccountHasHist(n)) buscarHist(n); else buscarLive(1, n);
  }, [cuenta, buscar, setCuenta, buscarHist, buscarLive, cmfAccountHasHist]);

  // Agregar una cuenta que no figura en el listado: se commitea y se abre el
  // diálogo de etiqueta (definido después de buscarCon para no caer en la TDZ).
  const agregarCuenta = useCallback((n: string) => {
    // Commitea la cuenta (URL, storage, fuente) y abre el diálogo de etiqueta;
    // el título del diálogo lee `cuenta`, que ya está actualizada al renderizar.
    buscarCon(n);
    setLabelDraft("");
    setLabelOpen(true);
  }, [buscarCon]);

  // Cambio de fuente → re-búsqueda inmediata con la nueva (setState es async,
  // así que despachamos directo a la función correspondiente).
  const cambiarFuente = useCallback((v: Fuente) => {
    setFuente(v);
    if (v === "hist") buscarHist(); else buscarLive(1);
  }, [buscarHist, buscarLive]);

  // Búsqueda inicial automática con la cuenta por defecto.
  useEffect(() => { buscar(1); /* eslint-disable-next-line */ }, []);

  const rows: Movimiento[] = fuente === "hist" ? histRows : movs;

  // Predicado de filtro client-side (compartido con el export CSV).
  const matches = useCallback((m: Movimiento) => {
    const needle = q.trim().toLowerCase();
    const min = montoMin.trim() ? Math.abs(parseMonto(montoMin)) : null;
    const max = montoMax.trim() ? Math.abs(parseMonto(montoMax)) : null;
    const monto = parseMonto(m.monto);
    if (tipo === "deb"  && monto >= 0) return false;
    if (tipo === "cred" && monto <  0) return false;
    const abs = Math.abs(monto);
    if (min != null && abs < min) return false;
    if (max != null && abs > max) return false;
    if (needle) {
      const hay = [
        m.concepto, m.detalle ?? "", m.comprobante,
        m.originante?.nro_documento ?? "",
      ].join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }, [q, tipo, montoMin, montoMax]);

  const filtered = useMemo(() => rows.filter(matches), [rows, matches]);

  // Export CSV: en histórico agota el cursor primero (rango completo); en vivo
  // exporta la página visible. Respeta los filtros client-side activos.
  // Si el rango supera las 50k filas NO descarga un archivo cortado: delega al
  // backend, que genera el CSV completo y lo manda por mail (link S3, 6 hs).
  const exportCsv = useCallback(async () => {
    setExporting(true); setError(null); setInfo(null);
    try {
      const all: Movimiento[] = [...rows];
      if (fuente === "hist") {
        let cur = histCursor;
        while (cur && all.length < 50_000) {   // tope del export directo
          const r = await getMovimientosHistory({ cuenta, from: desde, to: hasta, order: "desc", limit: 500, cursor: cur });
          all.push(...r.movimientos);
          cur = r.next_cursor;
        }
        if (cur) {
          const { email } = await requestMovimientosExport({ cuenta, from: desde, to: hasta });
          setInfo(`El rango supera las 50.000 filas del export directo. El archivo completo se está ` +
                  `generando en el servidor: te va a llegar un mail a ${email} con el link de descarga ` +
                  `(válido 6 horas) cuando termine. Ojo: ese archivo trae el rango completo, sin los ` +
                  `filtros de texto de esta página.`);
          return;
        }
      }
      const out = all.filter(matches);
      downloadCsv(buildCsv(out), `movimientos-cmf-${cuenta}_${desde}_${hasta}.csv`);
    } catch (e) {
      setError(String(e));
    } finally {
      setExporting(false);
    }
  }, [rows, fuente, histCursor, cuenta, desde, hasta, matches]);

  // Totales sobre lo cargado y filtrado (en vivo: solo la página actual).
  const totals = useMemo(() => {
    let deb = 0, cred = 0;
    for (const m of filtered) {
      const v = parseMonto(m.monto);
      if (v < 0) deb += v; else cred += v;
    }
    return { count: filtered.length, deb, cred, neto: deb + cred };
  }, [filtered]);

  const hasClientFilter = q.trim() !== "" || tipo !== "todos" || montoMin !== "" || montoMax !== "";
  const pageRows = filtered;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h5">Movimientos</Typography>
        <Typography sx={{ color: "text.secondary", fontFamily: MONO, fontSize: 14 }}>· cuenta {cuenta || "—"}</Typography>
        {CMF_CUENTA_RE.test(cuenta) && (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            {cmfAccountLabel(cuenta) ? (
              <Chip size="small" label={cmfAccountLabel(cuenta)}
                    sx={{ height: 20, fontSize: 11, bgcolor: BRAND.primaryLight, color: BRAND.primaryDark }} />
            ) : (
              <Chip size="small" label="sin etiqueta" variant="outlined"
                    sx={{ height: 20, fontSize: 11, color: "text.disabled", borderStyle: "dashed" }} />
            )}
            <Tooltip title="Editar la etiqueta de esta cuenta (se guarda en el servidor, la ven todos)">
              <IconButton size="small" onClick={abrirLabel} sx={{ p: 0.4, color: BRAND.primary }}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <Dialog open={labelOpen} onClose={() => !labelSaving && setLabelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Etiqueta de la cuenta {cuenta}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Etiqueta" value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value.slice(0, 60))}
            onKeyDown={(e) => { if (e.key === "Enter" && !labelSaving) guardarLabel(); }}
            helperText={`${labelDraft.length}/60 · vacío borra la etiqueta. Se guarda en el servidor y la ven todos los operadores.`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelOpen(false)} disabled={labelSaving}>Cancelar</Button>
          <Button variant="contained" onClick={guardarLabel} disabled={labelSaving}>
            {labelSaving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ""}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      {!cuentaTieneHist && CMF_CUENTA_RE.test(cuenta) && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          El histórico local (base propia, ~{HIST_DEPTH_DAYS} días) sólo cubre la cuenta {CUENTA_DEFAULT}
          {cmfAccountLabel(CUENTA_DEFAULT) ? ` (${cmfAccountLabel(CUENTA_DEFAULT)})` : ""}. La cuenta {cuenta} se
          consulta en vivo contra el banco: 10 movimientos por página y totales sólo de la página visible.
        </Alert>
      )}

      {/* ---- Query (servidor): cuenta + rango de fechas ---- */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 1.5, borderColor: "#E6E2F7" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap>
          {/* Cuenta + Desde + Hasta siempre en una misma fila (anchos fijos: nunca
              son campos anchos). En pantallas angostas el Stack padre pasa a
              columna y estiraba cada campo a todo el ancho, uno por renglón. */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <Autocomplete
              freeSolo disableClearable
              options={opcionesCuenta}
              value={cuenta}
              inputValue={cuentaInput}
              // Tipeo libre: sólo dígitos. La cuenta se commitea (URL, storage,
              // fuente) al elegir una opción, al dar Enter, al salir del campo o
              // al apretar Buscar; no en cada tecla, para no buscar un número a
              // medias si Enter selecciona una opción resaltada.
              onInputChange={(_, v) => setCuentaInput(v.replace(/\D/g, ""))}
              // Enter con texto nuevo o clic en una opción → buscar con esa cuenta.
              // (MUI no dispara onChange si el valor no cambió: eso lo cubre el
              // onKeyDown de abajo.)
              onChange={(_, v) => {
                if (typeof v !== "string") return;
                if (v.startsWith(ADD_PREFIX)) agregarCuenta(v.slice(ADD_PREFIX.length));
                else buscarCon(v);
              }}
              filterOptions={(opts, state) => {
                const q = state.inputValue.replace(/\D/g, "");
                const hits = q ? opts.filter((o) => o.includes(q)) : opts;
                // Número válido que no figura → ofrecer agregarlo al listado.
                if (CMF_CUENTA_RE.test(q) && !opts.includes(q)) hits.push(ADD_PREFIX + q);
                return hits;
              }}
              getOptionLabel={(o) => (o.startsWith(ADD_PREFIX) ? o.slice(ADD_PREFIX.length) : String(o))}
              renderOption={(props, o) => {
                if (o.startsWith(ADD_PREFIX)) {
                  const n = o.slice(ADD_PREFIX.length);
                  return (
                    <li {...props} key={o}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", color: BRAND.primary }}>
                        <AddIcon sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontSize: 13 }}>
                          Agregar la cuenta <Box component="span" sx={{ fontFamily: MONO }}>{n}</Box> al listado (ponerle etiqueta)
                        </Typography>
                      </Stack>
                    </li>
                  );
                }
                const label = cmfAccountLabel(o);
                return (
                  <li {...props} key={o}>
                    <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ width: "100%" }}>
                      <Typography sx={{ fontFamily: MONO, fontSize: 14 }}>{o}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", flex: 1 }}>
                        {label ?? "cuenta del convenio (según el banco)"}
                      </Typography>
                      {cmfAccountHasHist(o) && (
                        <Chip size="small" label="histórico" sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Stack>
                  </li>
                );
              }}
              sx={{ width: 260 }}
              renderInput={(params) => (
                <TextField
                  {...params} label="Cuenta" placeholder="Elegí o escribí un N° de cuenta"
                  helperText={
                    cuenta && !CMF_CUENTA_RE.test(cuenta) ? "Solo dígitos (6 a 12)"
                    : cmfAccountLabel(cuenta) ?? (cuenta && !opcionesCuenta.includes(cuenta)
                        ? "No está en el listado: ponele etiqueta con el lapicito para que quede guardada"
                        : " ")
                  }
                  error={!!cuenta && !CMF_CUENTA_RE.test(cuenta)}
                  sx={{ "& input": { fontFamily: MONO } }}
                  onKeyDown={(e) => { if (e.key === "Enter" && cuentaInput === cuenta) buscar(1); }}
                  onBlur={() => { if (cuentaInput !== cuenta) setCuenta(cuentaInput); }}
                />
              )}
            />
            <TextField type="date" label="Desde" InputLabelProps={{ shrink: true }} value={desde} onChange={(e) => setDesde(e.target.value)} sx={{ width: 180 }} />
            <TextField type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={hasta} onChange={(e) => setHasta(e.target.value)} sx={{ width: 180 }} />
          </Stack>
          <Button variant="contained" onClick={() => buscarCon(cuentaInput)} disabled={loading} sx={{ minWidth: 120 }}>
            {loading ? "Buscando…" : "Buscar"}
          </Button>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          {/* Fuente de datos: un solo renglón, misma altura que el botón Buscar.
              El caption va a la derecha en una línea (no debajo) y el tooltip
              explica cada opción; el botón deshabilitado conserva el tooltip
              gracias al <span> envolvente. */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
            <ToggleButtonGroup
              size="small" exclusive value={fuente}
              onChange={(_, v) => v && cambiarFuente(v)}
              sx={{
                height: 36.5,
                "& .MuiToggleButton-root": {
                  textTransform: "none", px: 1.5, lineHeight: 1.2, whiteSpace: "nowrap", fontSize: 13,
                  "&.Mui-selected": { bgcolor: BRAND.primaryLight, color: BRAND.primaryDark, fontWeight: 700 },
                },
                "& .MuiToggleButtonGroup-grouped:first-of-type": { borderTopLeftRadius: 999, borderBottomLeftRadius: 999 },
                "& .MuiToggleButtonGroup-grouped:last-of-type":  { borderTopRightRadius: 999, borderBottomRightRadius: 999 },
              }}
            >
              <Tooltip title={cuentaTieneHist
                ? `Base local (últimos ~${HIST_DEPTH_DAYS} días, actualizada cada ~1h). Rango completo de una, sin esperas.`
                : `Sólo la cuenta ${CUENTA_DEFAULT} tiene histórico local. Esta cuenta se consulta en vivo.`}>
                <span style={{ display: "inline-flex" }}>
                  <ToggleButton value="hist" disabled={!cuentaTieneHist} sx={{ height: "100%" }}>Histórico</ToggleButton>
                </span>
              </Tooltip>
              <Tooltip title="Consulta directa al banco en tiempo real (lenta: 10 movs por página).">
                <ToggleButton value="live">En vivo</ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {fuente === "hist" ? "base local · act. ~1h" : "banco en tiempo real · 10/pág"}
            </Typography>
          </Stack>
          <Button
            variant="outlined" startIcon={<FileDownloadIcon />}
            onClick={exportCsv} disabled={loading || exporting || !didSearch}
            sx={{ minWidth: 110 }}
          >
            {exporting ? "Exportando…" : "CSV"}
          </Button>
        </Stack>
      </Paper>

      {/* ---- Filtros client-side sobre los resultados ---- */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 1.5, borderColor: "#EFEDF8", bgcolor: "#FBFAFE" }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems={{ lg: "center" }}>
          <TextField
            size="small" placeholder="Filtrar por concepto, detalle o comprobante…"
            value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ flex: 1, minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: BRAND.primary }} /></InputAdornment>,
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQ("")}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" label="Monto mín." value={montoMin} onChange={(e) => setMontoMin(e.target.value)}
              sx={{ width: 130, "& input": { fontFamily: MONO } }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            <TextField size="small" label="Monto máx." value={montoMax} onChange={(e) => setMontoMax(e.target.value)}
              sx={{ width: 130, "& input": { fontFamily: MONO } }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Stack>
          <ToggleButtonGroup
            size="small" exclusive value={tipo}
            onChange={(_, v) => v && setTipo(v)}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, borderRadius: 999 } }}
          >
            <ToggleButton value="todos">Todos</ToggleButton>
            <ToggleButton value="cred" sx={{ "&.Mui-selected": { color: CREDITO, bgcolor: "#E8F5EE" } }}>Créditos</ToggleButton>
            <ToggleButton value="deb"  sx={{ "&.Mui-selected": { color: DEBITO,  bgcolor: "#FBEAE9" } }}>Débitos</ToggleButton>
          </ToggleButtonGroup>
          {hasClientFilter && (
            <Button size="small" onClick={() => { setQ(""); setTipo("todos"); setMontoMin(""); setMontoMax(""); }}
              sx={{ color: "text.secondary" }}>Limpiar</Button>
          )}
        </Stack>
      </Paper>

      {/* ---- Barra de totales (solo de la página visible) ---- */}
      {didSearch && !loading && (
        <Stack direction="row" spacing={0} alignItems="stretch"
          sx={{ mb: 1.5, borderRadius: 3, overflow: "hidden", border: "1px solid #EFEDF8", bgcolor: "#fff" }}>
          <Stat
            label={fuente === "hist"
              ? (hasClientFilter ? `Filtrados (de ${rows.length})` : histCursor ? "Cargados (hay más)" : "Total del rango")
              : (hasClientFilter ? `En esta pág. (de ${rows.length})` : "En esta página")}
            value={String(totals.count)} mono />
          <Divider orientation="vertical" flexItem />
          <Stat label="Débitos"  value={`$ ${fmtMonto(totals.deb)}`}  color={DEBITO} />
          <Divider orientation="vertical" flexItem />
          <Stat label="Créditos" value={`$ ${fmtMonto(totals.cred)}`} color={CREDITO} />
          <Divider orientation="vertical" flexItem />
          <Stat label="Neto" value={`$ ${fmtMonto(totals.neto)}`} color={totals.neto < 0 ? DEBITO : CREDITO} strong />
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          {errorDetail && (
            <Box component="details" sx={{ mt: 0.75, "& summary": { cursor: "pointer", fontSize: 12, color: "text.secondary" } }}>
              <summary>Ver respuesta cruda del banco</summary>
              <Typography component="pre" sx={{ m: 0, mt: 0.5, fontFamily: MONO, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {errorDetail}
              </Typography>
            </Box>
          )}
        </Alert>
      )}
      {info && <Alert severity="info" onClose={() => setInfo(null)} sx={{ mb: 2 }}>{info}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, borderColor: "#EFEDF8" }}>
        <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow sx={{ "& th": { bgcolor: BRAND.primaryLight } }}>
              <TableCell sx={thSx}>Fecha</TableCell>
              <TableCell sx={thSx}>Concepto</TableCell>
              <TableCell sx={thSx}>Detalle / Originante</TableCell>
              <TableCell sx={thSx} align="right">Comprob.</TableCell>
              <TableCell sx={thSx} align="right">Monto</TableCell>
              <TableCell sx={thSx} align="right">Saldo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                {rows.length > 0 ? "Ningún movimiento coincide con el filtro."
                  : fuente === "hist" && desde < isoDaysAgo(HIST_DEPTH_DAYS)
                    ? `Sin movimientos en el histórico local (cubre ~${HIST_DEPTH_DAYS} días). Para fechas anteriores probá "En vivo".`
                    : "Sin movimientos en el período."}
              </TableCell></TableRow>
            )}
            {!loading && pageRows.map((m, i) => {
              const monto = parseMonto(m.monto);
              const esDeb = monto < 0;
              const { code, text } = splitConcepto(m.concepto);
              const f = fmtFecha(m.fecha_hora);
              const detalle = m.detalle || (m.originante?.nro_documento ? `${m.originante.tipo_documento}: ${m.originante.nro_documento}` : "—");
              return (
                <TableRow key={i} hover sx={{ "&:nth-of-type(even)": { bgcolor: "#FCFBFE" } }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{f.date}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", fontFamily: MONO }}>{f.time}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {code && <Chip label={code} size="small" sx={{ height: 18, fontSize: 10, fontFamily: MONO, bgcolor: BRAND.primaryLight, color: BRAND.primaryDark }} />}
                      <Typography sx={{ fontSize: 13, lineHeight: 1.3 }}>{text}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 380 }}>
                    <Tooltip title={detalle} placement="top-start" enterDelay={400}>
                      <Typography sx={{
                        fontSize: 11.5, color: "text.secondary", fontFamily: MONO, lineHeight: 1.4,
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>{detalle}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: 12, fontFamily: MONO, color: "text.secondary" }}>{m.comprobante || "—"}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: MONO, fontVariantNumeric: "tabular-nums", color: esDeb ? DEBITO : CREDITO, whiteSpace: "nowrap" }}>
                      {esDeb ? "−" : "+"} $ {fmtMonto(Math.abs(monto))}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: 12.5, fontFamily: MONO, fontVariantNumeric: "tabular-nums", color: "text.secondary", whiteSpace: "nowrap" }}>
                      $ {fmtMonto(parseMonto(m.saldo_parcial))}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación live (página a página contra el banco) */}
      {didSearch && fuente === "live" && totalPaginas > 1 && (
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <IconButton size="small" disabled={pagina <= 1 || loading} onClick={() => buscarLive(pagina - 1)}><ChevronLeftIcon /></IconButton>
          <Typography sx={{ fontSize: 13, fontFamily: MONO }}>Página {pagina} / {totalPaginas}</Typography>
          <IconButton size="small" disabled={pagina >= totalPaginas || loading} onClick={() => buscarLive(pagina + 1)}><ChevronRightIcon /></IconButton>
        </Stack>
      )}

      {/* Histórico: append incremental via cursor */}
      {didSearch && fuente === "hist" && histCursor && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={cargarMasHist} disabled={loading}>
            {loading ? "Cargando…" : `Cargar 500 más (${histRows.length} cargados)`}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

function Stat({ label, value, color, mono, strong }: { label: string; value: string; color?: string; mono?: boolean; strong?: boolean }) {
  return (
    <Box sx={{ flex: 1, px: 2.5, py: 1.25 }}>
      <Typography sx={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase", color: "text.secondary", fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: strong ? 18 : 15, fontWeight: strong ? 800 : 700, fontFamily: (mono || true) ? MONO : undefined, fontVariantNumeric: "tabular-nums", color: color ?? "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );
}
